// Utilidad para enmascarar información sensible
// Muestra ~40% de los caracteres (inicio + final), oculta >50%

/**
 * Enmascara un email mostrando ~40% de caracteres
 * @param {string} email - Email a enmascarar
 * @returns {string} Email enmascarado
 *
 * @example
 * maskEmail('jorgealvarez@gmail.com') // 'jor***ez@gm***.com'
 * maskEmail('ab@x.com') // 'a***@x.com'
 * maskEmail('jorge@gmail.com') // 'jo**e@gm**.com'
 */
export const maskEmail = (email) => {
  if (!email || typeof email !== 'string') return '';

  const [user, domainPart] = email.split('@');
  if (!domainPart) return email; // Email inválido

  const [domain, ...tldParts] = domainPart.split('.');
  const tld = tldParts.join('.'); // Por si hay dominios como .co.uk

  // Calcular cuántos caracteres mostrar (40% aprox)
  const userVisibleCount = Math.max(1, Math.floor(user.length * 0.4));
  const domainVisibleCount = Math.max(1, Math.floor(domain.length * 0.4));

  // Dividir los caracteres visibles entre inicio y final
  const userStart = Math.ceil(userVisibleCount / 2);
  const userEnd = Math.floor(userVisibleCount / 2);
  const domainStart = Math.ceil(domainVisibleCount / 2);
  const domainEnd = Math.floor(domainVisibleCount / 2);

  // Construir user enmascarado
  let maskedUser;
  if (user.length <= 2) {
    maskedUser = user.charAt(0) + '***';
  } else if (userEnd === 0) {
    maskedUser = user.substring(0, userStart) + '***';
  } else {
    maskedUser = user.substring(0, userStart) + '***' + user.substring(user.length - userEnd);
  }

  // Construir dominio enmascarado
  let maskedDomain;
  if (domain.length <= 2) {
    maskedDomain = domain.charAt(0) + '***';
  } else if (domainEnd === 0) {
    maskedDomain = domain.substring(0, domainStart) + '***';
  } else {
    maskedDomain = domain.substring(0, domainStart) + '***' + domain.substring(domain.length - domainEnd);
  }

  return `${maskedUser}@${maskedDomain}.${tld}`;
};

/**
 * Enmascara un teléfono mostrando ~40% de caracteres
 * @param {string} phone - Teléfono a enmascarar
 * @returns {string} Teléfono enmascarado
 *
 * @example
 * maskPhone('612345678') // '612***78'
 * maskPhone('912345678') // '912***78'
 * maskPhone('+34 612 345 678') // '+34 612***78'
 */
export const maskPhone = (phone) => {
  if (!phone || typeof phone !== 'string') return '';

  // Eliminar espacios y caracteres especiales para calcular
  const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');

  // Calcular cuántos caracteres mostrar (40% aprox, mínimo 3)
  const visibleCount = Math.max(3, Math.floor(cleanPhone.length * 0.4));

  // Dividir entre inicio y final
  const startCount = Math.ceil(visibleCount / 2);
  const endCount = Math.floor(visibleCount / 2);

  // Construir enmascarado
  if (cleanPhone.length <= 4) {
    return cleanPhone.charAt(0) + '***';
  }

  const start = cleanPhone.substring(0, startCount);
  const end = endCount > 0 ? cleanPhone.substring(cleanPhone.length - endCount) : '';

  return end ? `${start}***${end}` : `${start}***`;
};

/**
 * Determina si un usuario está en modo espectador
 * (útil para centralizar la lógica de enmascarado)
 * @param {boolean} isAuthenticated - Si el usuario está autenticado como admin
 * @param {boolean} hasValidToken - Si tiene un token de espectador válido
 * @returns {boolean} true si es espectador (debe enmascarse la info)
 */
export const isSpectatorMode = (isAuthenticated, hasValidToken) => {
  return !isAuthenticated && hasValidToken;
};
